import {
  defineStyle,
  defineStyleConfig,
  Flex,
  FormControl,
  FormLabel,
  FormLabelProps,
  FormErrorMessage,
  forwardRef,
  Textarea as ChakraTextArea,
  TextareaProps as ChakraTextareaProp
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { isNumber, isUndefined } from 'lodash';

import { CommonFormProps } from '@/components/atomic/v1/types';

const outline = defineStyle({
  border: '2px solid',
  borderColor: 'form/border',
  borderRadius: '4px',
  bgColor: 'form/bg',
  padding: '12px',
  boxShadow: 'none',
  _placeholder: {
    color: 'text/disabled'
  },
  _hover: {
    borderColor: 'form/border:hover',
    bgColor: 'form/bg:hover',
    _disabled: {
      cursor: 'not-allowed',
      bgColor: 'form/bg:disable',
      borderColor: 'form/border'
    },
    _invalid: {
      borderColor: 'form/border:error',
      bgColor: 'form/bg:error'
    },
    _readOnly: {
      cursor: 'not-allowed',
      bgColor: 'form/bg:readonly',
      borderColor: 'form/border'
    }
  },
  _active: {
    borderColor: 'form/border:hover',
    bgColor: 'form/bg:hover',
    boxShadow: 'none',
    _readOnly: {
      bgColor: 'form/bg:readonly',
      borderColor: 'form/border'
    }
  },
  _focus: {
    borderColor: 'form/border:hover',
    bgColor: 'form/bg:hover',
    boxShadow: 'none',
    _invalid: {
      borderColor: 'form/border:error',
      bgColor: 'form/bg:error'
    },
    _readOnly: {
      bgColor: 'form/bg:readonly',
      borderColor: 'form/border'
    }
  },
  _disabled: {
    bgColor: 'form/bg:disable'
  },
  _invalid: {
    borderColor: 'form/border:error',
    bgColor: 'form/bg:error',
    boxShadow: 'none'
  },
  _readOnly: {
    bgColor: 'form/bg:readonly'
  }
});

export const textareaTheme = defineStyleConfig({
  variants: { outline }
});

export type TextareaProps = Omit<ChakraTextareaProp, 'isInvalid'> & CommonFormProps & { labelProps?: FormLabelProps };

function getLengthFromValue(value: ChakraTextareaProp['value']) {
  if (isNumber(value)) {
    return `${value}`.length;
  }

  return value?.length ?? 0;
}

const Textarea = forwardRef<TextareaProps, 'textarea'>(
  (
    {
      id,
      name,
      onChange,
      isReadOnly = false,
      isRequired = false,
      isDisabled = false,
      maxLength,
      value,
      width = '100%',
      label,
      labelProps = {},
      error,
      ...rest
    },
    ref
  ) => {
    const [length, setLength] = useState(getLengthFromValue(value));

    useEffect(() => {
      if (!isUndefined(value) && getLengthFromValue(value) === 0) {
        setLength(0);
      }
    }, [value]);

    return (
      <FormControl isInvalid={!!error} isReadOnly={isReadOnly} isRequired={isRequired} isDisabled={isDisabled} id={id}>
        {label && <FormLabel {...labelProps}>{label}</FormLabel>}
        <Flex flexDir="column" width={width} maxWidth="100%">
          <ChakraTextArea
            name={name}
            ref={ref}
            onChange={(e) => {
              setLength(e.target.value.length);
              onChange && onChange(e);
            }}
            maxLength={maxLength}
            value={value}
            {...rest}
            data-testid={`textarea-${name ?? ''}`}
          />
          <Flex justifyContent="space-between">
            {error && <FormErrorMessage mt={1}>{error}</FormErrorMessage>}
            {maxLength && (
              <Flex as="span" textStyle="body.small" alignItems="center" marginLeft="auto" mt={1}>
                {`${length}/${maxLength}`}
              </Flex>
            )}
          </Flex>
        </Flex>
      </FormControl>
    );
  }
);

export default Textarea;
